import { useApolloClient } from "@apollo/client";
import {
  Button,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuList,
  Portal,
  Spacer,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useState } from "react";
import {
  RiAddFill,
  RiArrowDownSLine,
  RiCheckLine,
  RiDownloadLine,
  RiDraftLine,
  RiUploadLine,
} from "react-icons/ri";
import { EmptyState } from "src/components/EmptyState";
import { AdminFormButtons } from "src/components/Layout/AdminFormButtons";
import { ConditionalTooltip } from "src/components/Layout/ConditionalTooltip";
import { MenuItemWithDescription } from "src/components/Menus/MenuItemWithDescription";
import { WithRequiredHasuraRoles } from "src/components/Permissions/WithRequiredHasuraRoles";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import * as FSQ from "src/generated/hooks/form-service/queries";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { triggerDownload } from "src/services/dataTransfer";
import { maybePluralize } from "src/services/format";
import * as FormTemplate from "src/services/formTemplate";
import * as GQL from "src/types/graphql";
import { HasuraRole } from "src/types/hasuraRole";
import { EditFormTemplateBackButton } from "../../components/BackButton";
import { useFormTemplateContext } from "../../formTemplateContext";
import { GET_FORM_TEMPLATE_BY_ID_FOR_EDIT } from "../../graphql/queries";
import { AddSectionButton } from "./components/AddSectionButton";
import { ImportError } from "./components/ImportError";
import { ValidationAlert } from "./components/ValidationAlert";
import { useCalculateDiff, useEditFormTemplateContext } from "./context";
import { FormButtons } from "./FormButtons";
import client from "./FormTemplateClient";
import { FormTemplateSection } from "./FormTemplateSection";
import { ImportFormTemplateDialog } from "./ImportFormTemplateDialog";
import { useCheckPendingQuestionEdits } from "./useCheckPendingQuestionEdits";

type Props = {
  organizationId: uuid;
  onImportFromJSON: () => void;
};

export const Content: React.FC<Props> = ({
  organizationId,
  onImportFromJSON,
}) => {
  const flags = useFlags(["form-template-content-export-import"]);
  const {
    state,
    formTemplateId,
    dispatch,
    saveFormSchema,
    hasUnsavedChanges: dirty,
    hasUnpublishedChanges,
    isPublishedMode,
  } = useEditFormTemplateContext();
  const { formTemplate, menuRef, actionRef, mode } = useFormTemplateContext();
  const { id, sections } = state.get("draft");
  const publish = FSQ.usePublishFormSchema();
  const apolloClient = useApolloClient();
  const queryClient = useQueryClient();
  const [isPublishing, setPublishing] = useState(false);
  const { checkPendingQuestionEdits } = useCheckPendingQuestionEdits(
    state,
    dispatch
  );
  const invalidateFormTemplate = client.useInvalidateFormTemplate();
  const avelaToast = useAvelaToast();
  const { confirm, confirmationDialog } = useConfirmationDialog({
    header: `Unsaved changes?`,
    body: "You have unsaved changes. Would you like to save the changes?",
  });

  const calculateDiff = useCalculateDiff();

  const onPublish = useCallback(async () => {
    try {
      const validations = checkPendingQuestionEdits();
      if (!validations.success) {
        return;
      }

      let doSave = false;
      if (dirty) {
        if (
          !(await confirm({
            body: "You have unsaved changes. Would you like to save the changes and publish?",
            cancelButton: {
              label: "No, cancel",
            },
            confirmButton: { label: "Yes, save and publish" },
          }))
        ) {
          return;
        }
        doSave = true;
      } else {
        if (
          !(await confirm({
            header: "Publishing form",
            body: "Are you sure you want to publish this form? You won't be able to edit the form freely once it's published.",
            cancelButton: {
              label: "No, cancel",
            },
            confirmButton: { label: "Yes, publish form" },
          }))
        ) {
          return;
        }
      }

      setPublishing(true);
      avelaToast({
        id: PUBLISHING_TOAST,
        title: "Working hard",
        description: "Publishing form...",
        status: "loading",
        duration: null,
        isClosable: false,
      });

      if (doSave) {
        await saveFormSchema();
      }

      const response = await publish.mutateAsync({
        path: {
          formTemplateId,
        },
      });

      const errorCount = response.data?.data?.validationResults.length ?? 0;
      if (errorCount > 0) {
        dispatch({
          type: "SetValidations",
          validations: response.data?.data?.validationResults ?? [],
        });

        avelaToast.close(PUBLISHING_TOAST);
        avelaToast.error({
          title: `${errorCount} ${maybePluralize(
            errorCount,
            "error",
            "errors"
          )} found`,
          description: "",
        });

        return;
      }

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [FSQ.useGetFormTemplateKey, formTemplateId],
        }),
        apolloClient.refetchQueries({
          include: [GET_FORM_TEMPLATE_BY_ID_FOR_EDIT],
        }),
        calculateDiff.unsafeCalculateDiff({ force: true }).catch((error) => {
          // Swallow any error and log it. We don't want any error from diff calculation to trigger publishing error.
          console.error(error);
        }),
      ]);

      avelaToast({
        title: "Form published",
        description: "Your form has been published.",
        status: "success",
      });
    } catch (error) {
      console.error(error);
      avelaToast.genericError();
    } finally {
      avelaToast.close(PUBLISHING_TOAST);
      setPublishing(false);
    }
  }, [
    checkPendingQuestionEdits,
    dirty,
    avelaToast,
    publish,
    formTemplateId,
    queryClient,
    apolloClient,
    calculateDiff,
    confirm,
    saveFormSchema,
    dispatch,
  ]);

  const exportFormTemplate = FSQ.useExportFormTemplate(
    {
      path: {
        formTemplateId: formTemplate.id,
      },
    },
    undefined,
    {
      enabled: false,
    }
  );

  const [isExporting, setExporting] = useState(false);
  const exportFormTemplateJSON = async () => {
    if (flags["form-template-content-export-import"].enabled) {
      try {
        const validations = checkPendingQuestionEdits();
        if (!validations.success) {
          return;
        }

        let doSave = false;

        if (dirty) {
          if (
            !(await confirm({
              body: "You have unsaved changes. Would you like to save the changes and export JSON?",
              cancelButton: {
                label: "No, cancel",
              },
              confirmButton: { label: "Yes, save and export JSON" },
            }))
          ) {
            return;
          } else {
            doSave = true;
          }
        }

        setExporting(true);

        if (doSave) {
          await saveFormSchema();
        }

        const response = await exportFormTemplate.refetch();

        if (!response.data?.data) {
          avelaToast.genericError();
          return;
        }

        triggerDownload(
          new Blob([JSON.stringify(response.data.data, undefined, 2)], {
            type: "text/json",
          }),
          "formTemplate.json"
        );
      } catch (error) {
        console.error(error);
        avelaToast.genericError();
      } finally {
        setExporting(false);
      }
    } else {
      if (dirty) {
        if (
          !(await confirm({
            body: "You have unsaved changes. Would you like to save the changes and export JSON?",
            cancelButton: {
              label: "No, cancel",
            },
            confirmButton: { label: "Yes, save and export JSON" },
          }))
        ) {
          return;
        } else {
          await saveFormSchema();
        }
      }

      const json = FormTemplate.toWithoutId(state.get("draft"));

      triggerDownload(
        new Blob([JSON.stringify(json, undefined, 2)], {
          type: "text/json",
        }),
        "formTemplate.json"
      );
    }
  };

  const {
    isOpen: isImportFormDialogOpen,
    onOpen: onImportFormDialogOpen,
    onClose: onImportFormDialogClose,
  } = useDisclosure();

  const handleImportFromJSON = useCallback(async () => {
    if (!dirty) {
      onImportFromJSON();
      return;
    }

    if (
      await confirm({
        header: "Import from JSON",
        body: "You have unsaved changes. Would you like to discard the changes and import from JSON?",
        cancelButton: {
          label: "No, cancel",
        },
        confirmButton: { label: "Yes, import from JSON" },
      })
    ) {
      await invalidateFormTemplate(formTemplate.id);
      onImportFromJSON();
    }
  }, [
    dirty,
    confirm,
    onImportFromJSON,
    invalidateFormTemplate,
    formTemplate.id,
  ]);

  const handleImportFromExistingForm = useCallback(async () => {
    if (!dirty) {
      onImportFormDialogOpen();
      return;
    }

    if (
      await confirm({
        header: "Import from existing form",
        body: "You have unsaved changes. Would you like to discard the changes and import from an existing form?",
        cancelButton: {
          label: "No, cancel",
        },
        confirmButton: { label: "Yes, import from an existing form" },
      })
    ) {
      await invalidateFormTemplate(formTemplate.id);
      onImportFormDialogOpen();
    }
  }, [
    dirty,
    confirm,
    onImportFormDialogOpen,
    invalidateFormTemplate,
    formTemplate.id,
  ]);

  return (
    <>
      <ImportFormTemplateDialog
        onClose={onImportFormDialogClose}
        isOpen={isImportFormDialogOpen}
        targetFormTemplateId={formTemplate.id}
      />
      <Portal containerRef={menuRef}>
        <Flex alignItems="center" gap={2}>
          <Flex
            alignItems="center"
            gap={2}
            px={4}
            py={2}
            border="1px solid"
            borderColor="gray.200"
            borderRadius="md"
          >
            <Icon
              as={
                formTemplate.mode === GQL.form_template_mode_enum.Draft
                  ? RiDraftLine
                  : RiCheckLine
              }
              color="gray.600"
            />
            <Text fontWeight="600" fontSize="md" color="gray.700">
              {formTemplate.mode === GQL.form_template_mode_enum.Draft
                ? "Draft"
                : mode === "edit"
                ? "Published - Editing"
                : "Published"}
            </Text>
          </Flex>
          <Text fontSize="sm" color="gray.500">
            Form version: {formTemplate.schema_version}
          </Text>
        </Flex>
      </Portal>
      <Portal containerRef={actionRef}>
        {mode === "edit" ? (
          <ConditionalTooltip
            showTooltip={!hasUnpublishedChanges}
            label="No changes to publish"
          >
            <Button
              variant="solid"
              colorScheme="primary"
              onClick={onPublish}
              isDisabled={isPublishing || !hasUnpublishedChanges}
              isLoading={isPublishing}
            >
              Publish
            </Button>
          </ConditionalTooltip>
        ) : null}
      </Portal>
      {confirmationDialog}
      <Flex direction="column" gap={4} grow="1" position="relative">
        <WithRequiredHasuraRoles
          roles={[HasuraRole.ADMIN, HasuraRole.ORG_ADMIN]}
        >
          <Flex justifyContent="right" gap={4}>
            {flags["form-template-content-export-import"].enabled &&
              mode === "edit" && (
                <Menu>
                  <ConditionalTooltip
                    showTooltip={isPublishedMode}
                    label="Cannot import on to a published form"
                    placement="top"
                    hasArrow
                  >
                    <MenuButton
                      as={Button}
                      leftIcon={<RiUploadLine />}
                      rightIcon={<RiArrowDownSLine />}
                      variant="outline"
                      isDisabled={isPublishedMode}
                    >
                      Import
                    </MenuButton>
                  </ConditionalTooltip>
                  <MenuList>
                    <MenuItemWithDescription
                      onClick={handleImportFromJSON}
                      title=".JSON file"
                      description="Replace this form's structure from a file"
                    />
                    <MenuItemWithDescription
                      onClick={handleImportFromExistingForm}
                      title="From existing form"
                      description="Replace content from an existing form"
                    />
                  </MenuList>
                </Menu>
              )}
            {sections.length > 0 && (
              <Button
                leftIcon={<RiDownloadLine />}
                onClick={exportFormTemplateJSON}
                isLoading={isExporting}
              >
                Export JSON
              </Button>
            )}
          </Flex>
        </WithRequiredHasuraRoles>
        <ValidationAlert id={id} />
        <ImportError id={id} />
        {mode === "edit" && sections.length === 0 && (
          <AddSectionButton
            newOrder={0}
            leftIcon={<RiAddFill />}
            width="100%"
            height="5rem"
          />
        )}
        {mode === "view" && sections.length === 0 && (
          <Flex alignItems="center" height="10rem" justifyContent="center">
            <EmptyState
              heading=""
              description="This form template is empty"
              graphic={null}
            />
          </Flex>
        )}

        {sections.map((section, index) => {
          return (
            <FormTemplateSection
              key={index}
              section={section}
              organizationId={organizationId}
              order={index}
            />
          );
        })}
        <Spacer />
        <AdminFormButtons insideTabPanel>
          <EditFormTemplateBackButton />
          <Spacer />
          {mode === "edit" ? <FormButtons /> : null}
        </AdminFormButtons>
      </Flex>
    </>
  );
};

const PUBLISHING_TOAST = "publishing-toast";
